import React from "react";
import { IonIcon } from "@ionic/react";
import clsx from "clsx";
import { format } from "date-fns";

import archiveIcon from "../../assets/icons/archive.svg";
import downloadIcon from "../../assets/icons/download.svg";
import sharedBoxIcon from "../../assets/icons/shared_box_icon.svg";
import { IBoxDashboard } from "../../state";
import ModalConfirm from "../../molecules/ModalConfirm";
import ToolTip from "../ToolTip";

import styles from "./BoxAnalysisItem.module.scss";

export interface BoxAnalysisItemProps {
  className?: string;
  isMyBox?: boolean;
  isSingle?: boolean;
  boxData: IBoxDashboard;
  isSharedBox: boolean;
  onClick?: () => void;
  gearboxHiddenFunc: (id: number) => void;
  getRealizations4CSV: (id: number) => void;
  teacherId: number;
  calling: boolean;
}

const BoxAnalysisItem: React.FC<BoxAnalysisItemProps> = ({
  className = "",
  isMyBox = false,
  isSingle = false,
  teacherId,
  calling,
  boxData: {
    id,
    title,
    year,
    subject_id,
    students_count,
    name_status,
    teacher,
    realizations_count = 0,
    favorites_count = 0,
    comments_count = 0,
    checked_count,
    latest_realization_created_at,
    is_archived,
    is_submission_private,
    has_unread_realization = false,
  },
  isSharedBox,
  gearboxHiddenFunc,
  getRealizations4CSV,
  onClick,
}) => {
  const isTeacher = teacher.id === teacherId;
  const [state, setState] = React.useState({ showHiddenConfirm: false });
  const boxTitle = subject_id && year ? `${title}_${year}` : title;

  const counts = [realizations_count, favorites_count, comments_count];

  // BOX詳細のクリックを防ぎ、モーダルを表示する
  const handleHiddenClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setState({ showHiddenConfirm: true });
  };

  const handleConfirm = () => {
    gearboxHiddenFunc(id);
    setState({ showHiddenConfirm: false });
  };

  const handleDownloadCsv = (event: React.MouseEvent) => {
    event.stopPropagation(); // BOX詳細のクリックを防ぐ
    !calling && getRealizations4CSV(id);
  };

  const boxLabels = ["ストック", "いいね", "コメント"];

  return (
    <>
      <div className={styles.wrapper}>
        <div className={clsx(styles.boxItem, className)} onClick={onClick}>
          <div className={styles.boxInfo}>
            {!isSingle && (
              <>
                <div className={styles.label}>
                  <div className={styles.boxName}>
                    <p className={styles.title}>
                      {boxTitle}
                      {has_unread_realization && (
                        <span className={styles.badge} />
                      )}
                    </p>
                    {is_submission_private && (
                      <div className={styles.submissionPrivateContainer}>
                        <p className={styles.submissionPrivate}>非共有BOX</p>
                      </div>
                    )}
                  </div>
                  {!isSingle && latest_realization_created_at && (
                    <span className={styles.date}>
                      {"" + format(latest_realization_created_at, "yyyy.MM.dd")}
                      <span className={styles.slash}>/</span>
                      {name_status === "unpublish" && "匿名共有"}
                      {`(${students_count ?? "ー"}名)`}
                    </span>
                  )}
                </div>
                {checked_count !== null && (
                  <div className={styles.checkedCount}>
                    <span className={styles.num}>{checked_count}</span>
                  </div>
                )}
              </>
            )}
          </div>
          <div className={styles.count}>
            <div className={clsx(styles.allRealizationsCount)}>
              {counts.map((count, index) => (
                <div key={index} className={styles.actionCount}>
                  <span className={styles.label}>{boxLabels[index]}</span>
                  <span className={styles.value}>{count}</span>
                </div>
              ))}
            </div>
            <div className={styles.actionCountGroup}>
              {!isMyBox && (
                <>
                  <div
                    className={clsx(
                      styles.actionStatus,
                      styles.status,
                      is_archived && styles.archived,
                    )}
                  >
                    <span className={styles.label}>ステータス</span>
                    <span
                      className={clsx(
                        styles.value,
                        is_archived && styles.closed,
                      )}
                    >
                      {is_archived ? "募集終了" : "募集中"}
                    </span>
                  </div>
                  <p className={styles.actionTeacher}>
                    <span className={styles.label}>作成者</span>
                    <span className={styles.value}>{teacher.full_name}</span>
                  </p>
                </>
              )}
              <div className={styles.IconGroup}>
                <ToolTip content="csvダウンロード">
                  <IonIcon
                    className={styles.downloadIcon}
                    icon={downloadIcon}
                    onClick={handleDownloadCsv}
                  />
                </ToolTip>
                {isTeacher && (
                  <ToolTip content="BOX非表示">
                    <IonIcon
                      className={styles.archiveIcon}
                      icon={archiveIcon}
                      onClick={handleHiddenClick}
                    />
                  </ToolTip>
                )}
                {!isTeacher && isSharedBox && (
                  <ToolTip content="共有BOX">
                    <IonIcon
                      className={styles.sharedBoxIcon}
                      icon={sharedBoxIcon}
                    />
                  </ToolTip>
                )}
              </div>
            </div>
          </div>
        </div>
        <ModalConfirm
          loading={calling}
          isOpen={state.showHiddenConfirm}
          whiteMode
          message={
            <>
              このBOXを非表示にしますか？
              <br />
              非表示にしたBOXは、『BOXをつくる』ページの『編集』から再表示できます。
            </>
          }
          buttons={[
            {
              title: "キャンセル",
              type: "None",
              action: () => setState({ showHiddenConfirm: false }),
            },
            {
              title: "はい",
              type: "Success",
              action: handleConfirm,
            },
          ]}
        />
      </div>
    </>
  );
};

export default BoxAnalysisItem;
